import { useCallback, useState } from "react";
import {
    FaBook,
    FaCheckCircle,
    FaCode,
    FaCodeBranch,
    FaDownload,
    FaExclamationTriangle,
    FaListAlt,
    FaPaperclip,
    FaStickyNote,
    FaTags,
    FaTasks,
} from "react-icons/fa";
import { AppCodeBlock } from "../../../../../_CORE/APP/Templates/_UI/CodeBlock/_index";
import { AppButton } from "../../../../../_CORE/APP/Templates/_UI/Button/_index";
import {
    ReadOnlyInfo,
    ReadOnlyModalShell,
    ReadOnlySectionNav,
} from "../../../../../_CORE/APP/Templates/_UI/ReadOnly/_index";
import { StatusBadge } from "../../../../../_CORE/APP/Templates/_UI/Status/_index";
import { Badge, Chip } from "../../../../../_CORE/APP/Templates/_UI/Kit/_index";
import { ChangeDiffView } from "./ChangeDiffView";
import { UniversalFilePreviewModal } from "../../../../_UI/UniversalFilePreview";
import { downloadFilesAsZip } from "../../../../_UI/FileBatchDownload";
import "../../../../_UI/_Styles/FileCollection.css";
import { API_CORE } from "../../../../../_CORE/API/ApiCORE";
import { DOCUMENTS_CONSTANTS } from "../_MODULES/_API";
import {
    STATUS_MAP_LOGBOOK, STATUS_MAP_PRIORITY, OPTIONS_LOGBOOK_TYPE, onFindOptionLabel,
} from "../../../_Catalogs.Views";

const hasText = (value) => String(value ?? "").trim().length > 0;
const lineCount = (value) => hasText(value) ? String(value).split("\n").length : 0;

export const LogbookReadModal = ({ isOpen, entry, onClose }) => {
    const [useActiveSection, setActiveSection] = useState(null);
    const [usePreviewDocument, setPreviewDocument] = useState(null);
    const [useDownloadingFiles, setDownloadingFiles] = useState(false);

    const onLoadDocumentBlob = useCallback(async (documentFile) => {
        if (!documentFile?.id) throw new Error("Archivo sin identificador");
        const URL_RESOURCE = API_CORE.ENDPOINTS[DOCUMENTS_CONSTANTS.CONTENT].replace(":id", documentFile.id);
        const BLOB = await API_CORE.SERVICES.GET_RESOURCE(URL_RESOURCE, { responseType: "blob" });
        if (!(BLOB instanceof Blob)) throw new Error("No se pudo obtener el archivo");
        return BLOB;
    }, []);

    if (!entry) return null;

    const SECTION_PREFIX = `logbook-read-${entry.id}`;
    const sectionDomId = (id) => `${SECTION_PREFIX}-${id}`;

    const SECTIONS = [
        { id: "specification", label: "Especificación", icon: <FaListAlt />, isVisible: hasText(entry.specification) },
        { id: "problem", label: "Problema", icon: <FaExclamationTriangle />, isVisible: hasText(entry.problem) },
        { id: "solution", label: "Solución", icon: <FaCheckCircle />, isVisible: hasText(entry.solution) },
        { id: "notes", label: "Notas", icon: <FaStickyNote />, isVisible: hasText(entry.content) },
        { id: "code", label: "Código", icon: <FaCode />, count: lineCount(entry.code), isVisible: hasText(entry.code) },
        { id: "tags", label: "Etiquetas", icon: <FaTags />, count: entry.tags?.length ?? 0, isVisible: (entry.tags?.length ?? 0) > 0 },
        { id: "changes", label: "Cambios", icon: <FaCodeBranch />, count: entry.changes?.length ?? 0, isVisible: (entry.changes?.length ?? 0) > 0 },
        { id: "tasks", label: "Tareas", icon: <FaTasks />, count: entry.todos?.length ?? 0, isVisible: (entry.todos?.length ?? 0) > 0 },
        { id: "files", label: "Archivos", icon: <FaPaperclip />, count: entry.documents?.length ?? 0, isVisible: (entry.documents?.length ?? 0) > 0 },
    ].filter((section) => section.isVisible);

    const ACTIVE_SECTION = SECTIONS.some((section) => section.id === useActiveSection)
        ? useActiveSection
        : (SECTIONS[0]?.id ?? null);

    const onGoToSection = (id) => {
        setActiveSection(id);
        const TARGET = document.getElementById(sectionDomId(id));
        TARGET?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    const onDownloadDocument = async (documentFile) => {
        if (!documentFile?.id) return;
        const URL_RESOURCE = API_CORE.ENDPOINTS[DOCUMENTS_CONSTANTS.DOWNLOAD].replace(":id", documentFile.id);
        const BLOB = await API_CORE.SERVICES.GET_RESOURCE(URL_RESOURCE, { responseType: "blob" });
        if (!(BLOB instanceof Blob)) return;
        const objectUrl = window.URL.createObjectURL(BLOB);
        const link = window.document.createElement("a");
        link.href = objectUrl;
        link.download = documentFile.originalName || documentFile.title || "archivo";
        window.document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(objectUrl);
    };

    const onDownloadAllDocuments = async () => {
        if (!(entry.documents?.length) || useDownloadingFiles) return;
        setDownloadingFiles(true);
        try {
            await downloadFilesAsZip({
                files: entry.documents,
                loadBlob: onLoadDocumentBlob,
                fileName: `${entry.title || "bitacora"}-archivos.zip`,
                successMessage: `Se descargaron ${entry.documents.length} archivo${entry.documents.length === 1 ? "" : "s"}.`,
                errorMessage: "No se pudieron descargar los archivos de la entrada.",
            });
        } finally {
            setDownloadingFiles(false);
        }
    };

    const META = (
        <>
            <ReadOnlySectionNav
                items={SECTIONS}
                heading="En esta entrada"
                activeId={ACTIVE_SECTION}
                onSelect={onGoToSection}
            />

            <section className="LStudio__readonly_meta_section">
                <strong>Entrada</strong>
                <h3>{entry.title}</h3>
                <p>{onFindOptionLabel(OPTIONS_LOGBOOK_TYPE, entry.type)}</p>
            </section>

            <section className="LStudio__readonly_meta_section">
                <strong>Estado</strong>
                <div className="LStudio__readonly_meta_badges">
                    <StatusBadge status={entry.status} map={STATUS_MAP_LOGBOOK} size="sm" />
                    <StatusBadge status={entry.priority} map={STATUS_MAP_PRIORITY} size="sm" variant="outline" />
                </div>
            </section>

            <section className="LStudio__readonly_meta_section">
                <ReadOnlyInfo createdAt={entry.createdAt} updatedAt={entry.updatedAt} />
            </section>
        </>
    );

    return (
        <>
        <ReadOnlyModalShell
            isOpen={isOpen}
            onClose={onClose}
            icon={<FaBook />}
            title={entry.title || "Bitácora"}
            currentLabel="Entrada de bitácora"
            meta={META}
        >
            <article className="LStudio__readonly_document">
                <div className="LStudio__readonly_document_title">
                    <span><FaBook /></span>
                    <div>
                        <small>{onFindOptionLabel(OPTIONS_LOGBOOK_TYPE, entry.type)}</small>
                        <h1>{entry.title}</h1>
                    </div>
                </div>

                {hasText(entry.specification) && (
                    <section id={sectionDomId("specification")} className="LStudio__readonly_section">
                        <strong>Especificación</strong>
                        <p>{entry.specification}</p>
                    </section>
                )}

                {hasText(entry.problem) && (
                    <section id={sectionDomId("problem")} className="LStudio__readonly_section">
                        <strong>Problema</strong>
                        <p>{entry.problem}</p>
                    </section>
                )}

                {hasText(entry.solution) && (
                    <section id={sectionDomId("solution")} className="LStudio__readonly_section">
                        <strong>Solución</strong>
                        <p>{entry.solution}</p>
                    </section>
                )}

                {hasText(entry.content) && (
                    <section id={sectionDomId("notes")} className="LStudio__readonly_section">
                        <strong>Notas</strong>
                        <p>{entry.content}</p>
                    </section>
                )}

                {hasText(entry.code) && (
                    <section id={sectionDomId("code")} className="LStudio__readonly_section LStudio__readonly_section--code">
                        <strong>Código</strong>
                        <AppCodeBlock
                            code={entry.code}
                            language={entry.codeLanguage}
                            title={`Código (${entry.codeLanguage || "text"})`}
                            isPretty={false}
                            maxLines={36}
                        />
                    </section>
                )}

                {(entry.tags?.length ?? 0) > 0 && (
                    <section id={sectionDomId("tags")} className="LStudio__readonly_section">
                        <strong>Etiquetas</strong>
                        <div className="LStudio__readonly_chip_row">
                            {entry.tags.map((tag) => <Chip key={tag.id} tone="accent">{tag.name}</Chip>)}
                        </div>
                    </section>
                )}

                {(entry.changes?.length ?? 0) > 0 && (
                    <section id={sectionDomId("changes")} className="LStudio__readonly_section">
                        <strong><FaCodeBranch /> Cambios de código ({entry.changes.length})</strong>
                        <div className="LStudio__readonly_change_list">
                            {[...entry.changes]
                                .sort((a, b) => a.position - b.position)
                                .map((change) => <ChangeDiffView key={change.id} change={change} />)}
                        </div>
                    </section>
                )}

                {(entry.todos?.length ?? 0) > 0 && (
                    <section id={sectionDomId("tasks")} className="LStudio__readonly_section">
                        <strong><FaTasks /> Tareas</strong>
                        <div className="LStudio__readonly_todo_list">
                            {entry.todos.map((todo) => (
                                <div key={todo.id} data-done={todo.isDone ? "true" : "false"}>
                                    <span>{todo.isDone ? "✓" : "○"}</span>
                                    <p>{todo.text || todo.title}</p>
                                </div>
                            ))}
                        </div>
                    </section>
                )}

                {(entry.documents?.length ?? 0) > 0 && (
                    <section id={sectionDomId("files")} className="LStudio__readonly_section LStudio__app_file_collection">
                        <div className="LStudio__app_file_collection_header">
                            <strong><FaPaperclip /> Archivos</strong>
                            <AppButton size="sm" variant="ghost" tone="neutral" icon={<FaDownload />}
                                isLoading={useDownloadingFiles} onClick={onDownloadAllDocuments}>Descargar todos</AppButton>
                        </div>
                        <div className="LStudio__readonly_files LStudio__app_file_scroll_area">
                            {entry.documents.map((document) => (
                                <a key={document.id} href={document.url || "#"}
                                    onClick={(event) => { event.preventDefault(); setPreviewDocument(document); }}
                                    title="Abrir vista previa">
                                    <span>{document.title || document.originalName}</span>
                                    <Badge tone="neutral" size="sm">{document.kind || "Archivo"}</Badge>
                                </a>
                            ))}
                        </div>
                    </section>
                )}
            </article>
        </ReadOnlyModalShell>

        <UniversalFilePreviewModal
            file={usePreviewDocument}
            isOpen={!!usePreviewDocument}
            onClose={() => setPreviewDocument(null)}
            loadBlob={onLoadDocumentBlob}
            onDownload={onDownloadDocument}
        />
        </>
    );
};
